import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
// https://github.com/fullstackreact/google-maps-react#readme
// note existing repo uses google-maps-react - but it was flagging as errors because it's very old

// https://github.com/google-map-react/google-map-react
import GoogleMapReact, { fitBounds } from 'google-map-react';
import { useSearchStateLocationDispatch } from './hooks/useSearchStateLocation';
import { useSearchStateHits } from './hooks/useSearchStateHits';
import config from './config/config';
import { getInfoWindowString } from './helpers/searchMap';
import { useTabletAndBelowMediaQuery } from './hooks/useResponsive';
import IconTarget from '../ui/icons/IconTarget';

const CONTROL_ID_NEXT = `info-window-control-next`;
const CONTROL_ID_PREV = `info-window-control-prev`;

// #74961 - woods
// #79805 blue
const resetActiveIcons = ( markers ) => {
	// console.log( 'reset active icons' );
	markers.forEach( ( marker ) => {
		marker.setIcon( {
			url: config.MARKER_ICON,
		} );
	} );
};

// 74961
const setIconActive = ( marker ) => {
	// console.log( 'set icon active', marker.objectIDs );
	marker.setIcon( {
		url: config.MARKER_ICON_ACTIVE,
	} );
};
const setIconDefault = ( marker ) => {
	// console.log( 'reset icon', marker.objectIDs );
	marker.setIcon( {
		url: config.MARKER_ICON,
	} );
};

const SearchMap = ( props ) => {
	const isTabletAndBelow = useTabletAndBelowMediaQuery();
	const [ isOutOfBounds, setIsOutOfBounds ] = useState( false );
	const { activeHitId } = useSearchStateHits();
	const { hits, bounds, source } = props;
	const [ center, setCenter ] = useState( null );
	const [ zoom, setZoom ] = useState( 8 );
	const dispatch = useSearchStateLocationDispatch();
	const [ savedMap, setSavedMap ] = useState( null );
	const [ savedMaps, setSavedMaps ] = useState( null );
	const [ markers, setMarkers ] = useState( [] );
	const [ markerCluster, setMarkerCluster ] = useState( null );
	const [ openInfoWindowSaved, setOpenInfoWindowSaved ] = useState( null );
	const closeInfoWindowListener = useRef( null );
	const zoomListener = useRef( null );

	const carouselCurrentSlideIndex = useRef( 0 );

	const refMap = useRef( null );

	// make marker blue if hit hovered
	const activeHitMarker = useMemo( () => {
		const active = markers.find( ( m ) => m.objectIDs?.includes( activeHitId ) );
		return active;
	}, [ activeHitId, markers ] );

	useEffect( () => {
		resetActiveIcons( markers );
	}, [ markers, activeHitMarker ] );

	// update marker on hover of hit
	useEffect( () => {
		if ( openInfoWindowSaved ) {
			openInfoWindowSaved.close();
		}
		if ( activeHitMarker ) {
			setIconActive( activeHitMarker );
		}
	}, [ markers, activeHitMarker, openInfoWindowSaved ] );

	// update center and zoom
	useEffect( () => {
		if ( ! refMap.current ) {
			return;
		}
		// for one or only a few markers where lat or lng bounds match - fitBounds is incorrect
		// https://github.com/google-map-react/google-map-react/issues/207
		if ( bounds.sw.lat === bounds.ne.lat || bounds.sw.lng === bounds.ne.lng ) {
			setCenter( bounds.sw );
			setZoom( 12 );
			return;
		}

		// mobile is in a modal and is positioned 73px from the top
		const size = {
			width: refMap.current?.clientWidth - 15,
			height: isTabletAndBelow ? refMap.current?.clientHeight - 73 - 15 : refMap.current?.clientHeight - 15,
		};
		const { center: newCenter, zoom: newZoom } = fitBounds( bounds, size );
		// console.log( { newCenter, newZoom, bounds } );
		setCenter( newCenter );
		setZoom( source === 'api' ? newZoom : newZoom - 1 );
	}, [ isTabletAndBelow, bounds, savedMap, savedMaps, source ] );

	// update markers
	useEffect( () => {
		if ( ! savedMap || ! savedMaps ) {
			return;
		}
		setIsOutOfBounds( false );
		updateMarkers( { map: savedMap, maps: savedMaps, places: hits } );
	}, [ hits ] );

	const handleOnNext = ( { place, infoWindow } ) => {
		let newCurrentIndex = carouselCurrentSlideIndex.current + 1;
		if ( newCurrentIndex >= place.hits.length ) {
			newCurrentIndex = 0;
		}
		const newHit = place.hits[ newCurrentIndex ];
		carouselCurrentSlideIndex.current = newCurrentIndex;
		infoWindow.setContent( getInfoWindowString( {
			hit: newHit,
			count: place.hits.length,
			current: newCurrentIndex,
		} ) );
	};

	const handleOnPrev = ( { place, infoWindow } ) => {
		let newCurrentIndex = carouselCurrentSlideIndex.current - 1;
		if ( newCurrentIndex < 0 ) {
			newCurrentIndex = place.hits.length - 1;
		}
		const newHit = place.hits[ newCurrentIndex ];
		carouselCurrentSlideIndex.current = newCurrentIndex;
		infoWindow.setContent( getInfoWindowString( {
			hit: newHit,
			count: place.hits.length,
			current: newCurrentIndex,
		} ) );
	};

	const updateMarkers = ( { map, maps, places } ) => {
		// remove markers
		for ( let index = 0; index < markers.length; index++ ) {
			markers[ index ].setMap( null );
		}
		const markersNew = [];
		const infoWindowsNew = [];
		let openInfoWindow = null;

		const addPlace = ( { place, i } ) => {
			const isState = place.type === 'state';

			const createMarker = () => {
				return new maps.Marker( {
					position: {
						lat: place._geoloc.lat,
						lng: place._geoloc.lng,
					},
					...isState && { label: {
						text: place.alpha_2_code,
						className: 'marker-label',
						fontSize: '10px',
						color: '#545454',
					} },
					...( place.hits ) && { label: {
						text: `${ place.hits.length }`,
						className: 'marker-label',
						fontSize: '10px',
						color: '#545454',
					} },
					map,
					icon: {
						url: config.MARKER_ICON,
					},
					objectIDs: place.hits ? place.hits.map( ( hit ) => hit.objectID ) : [ place.objectID ],
					objectID: place.objectID,
					title: place.title,
					markerType: isState ? 'state' : 'one',
				} );
			};

			const attachMarkerListeners = ( ) => {
				marker.addListener( 'click', () => {
					if ( place.type === 'state' ) {
						dispatch( {
							type: 'selectState',
							stateAsLocation: {
								name: place.name,
								abbr: place.alpha_2_code.toLowerCase(),
							},
						} );
						return;
					}
					if ( marker.markerType === 'one' ) {
						if ( openInfoWindow ) {
							openInfoWindow.close();
						}
						resetActiveIcons( markersNew );
						setIconActive( marker );
						openInfoWindow = infoWindowsNew[ i ];
						setOpenInfoWindowSaved( infoWindowsNew[ i ] );
						openInfoWindow.open( map, marker );
						carouselCurrentSlideIndex.current = 0;
					}
				} );

				marker.addListener( 'mouseover', () => {
					setIconActive( marker );
				} );

				marker.addListener( 'mouseout', () => {
					// unless has open info window
					if ( openInfoWindow?.objectIDs.includes( marker.objectIDs[ 0 ] ) ) {
						return;
					}
					setIconDefault( marker );
				} );
			};

			const marker = createMarker();
			attachMarkerListeners( marker );
			markersNew.push( marker );

			if ( place.type !== 'state' ) {
				const infoWindowTemplate = getInfoWindowString( {
					hit: place.hits[ carouselCurrentSlideIndex.current ],
					count: place.hits.length,
					current: carouselCurrentSlideIndex.current,
				} );
				const infoWindow = new maps.InfoWindow( {
					content: infoWindowTemplate,
					maxWidth: 400,
					objectID: place.objectID,
					objectIDs: place.hits.map( ( hit ) => hit.objectID ),
				} );

				maps.event.addListener( infoWindow, 'closeclick', function() {
					resetActiveIcons( markersNew );
					const elNext = document.getElementById( CONTROL_ID_NEXT );
					maps.event.removeDomListener( elNext, 'click', handleOnNext );
					const elPrev = document.getElementById( CONTROL_ID_PREV );
					maps.event.removeDomListener( elPrev, 'click', handleOnPrev );
				} );

				// attach listner for carousel control buttons
				maps.event.addListener( infoWindow, 'domready', function() {
					// console.log( 'open infowindow', place );
					// add content again to get updated current slide
					infoWindow.setContent( getInfoWindowString( {
						hit: place.hits[ carouselCurrentSlideIndex.current ],
						count: place.hits.length,
						current: carouselCurrentSlideIndex.current,
					} ) );

					// Add control event listeners
					const elNext = document.getElementById( CONTROL_ID_NEXT );
					if ( elNext ) {
						maps.event.addDomListener( elNext, 'click', function() {
							handleOnNext( { place, infoWindow } );
						} );
					}
					const elPrev = document.getElementById( CONTROL_ID_PREV );
					if ( elPrev ) {
						maps.event.addDomListener( elPrev, 'click', function() {
							handleOnPrev( { place, infoWindow } );
						} );
					}
				} );

				infoWindowsNew.push( infoWindow );
			}
		};

		const placesWithCoords = places.filter( ( place ) => {
			return place._geoloc && place._geoloc.lat && place._geoloc.lng;
		} );

		placesWithCoords.forEach( ( place, i ) => {
			addPlace( { place, i } );
		} );

		// close any open info window on map click
		if ( closeInfoWindowListener.current ) {
			maps.event.removeListener( closeInfoWindowListener.current );
		}
		closeInfoWindowListener.current = maps.event.addListener( map, 'click', function() {
			if ( openInfoWindow ) {
				openInfoWindow.close();
				openInfoWindow = null;
				resetActiveIcons( markersNew );
			}
		} );

		// close any open info window on zoom
		if ( zoomListener.current ) {
			maps.event.removeListener( zoomListener.current );
		}
		maps.event.addListener( map, 'zoom_changed', function() {
			if ( openInfoWindow ) {
				openInfoWindow.close();
				openInfoWindow = null;
				resetActiveIcons( markersNew );
			}
		} );

		if ( markerCluster ) {
			markerCluster.clearMarkers();
			markerCluster.addMarkers( markersNew );
		} else {
			const cluster = new MarkerClusterer( {
				markers: markersNew,
				map,
				renderer: {
					render: ( { markers: markersInCluster, _position: position } ) => {
						const count = markersInCluster.reduce( ( acc, marker ) => {
							return marker.objectIDs ? acc + marker.objectIDs.length : acc + 1;
						}, 0 );
						return new maps.Marker( {
							position: {
								lat: position.lat(),
								lng: position.lng(),
							},
							label: {
								text: `${ count }`,
								className: 'marker-label',
								fontSize: '10px',
								color: '#545454',
							},
							icon: {
								url: config.MARKER_ICON,
							},
						} );
					},
				} } );
			setMarkerCluster( cluster );
		}
		setMarkers( markersNew );
	};

	const handleApiLoaded = ( map, maps, places ) => {
		setSavedMap( map );
		setSavedMaps( maps );
		updateMarkers( { map, maps, places } );
	};

	const handleDrag = () => {
		setIsOutOfBounds( true );
	};

	const handleReturnToBoundary = () => {
		savedMap.setCenter( center );
		savedMap.setZoom( zoom );
		setIsOutOfBounds( false );
	};

	const handleSelectNewLocation = () => {
		dispatch( { type: 'openLocationSelector' } );
	};

	return (
		<div id="map-search" ref={refMap} style={{
			height: isTabletAndBelow ? 'calc(100vh - 73px)' : '100vh',
			width: '100%',
		}}>
			{ isOutOfBounds && (
				<div className="tw-absolute tw-bottom-6 tw-z-10 tw-left-14 tw-right-14">
					<div className="tw-flex tw-justify-center ">
						<div className="tw-flex tw-items-center tw-bg-white tw-rounded-full tw-px-3 tw-space-x-1 tw-py-1">
							<IconTarget />
							<button
								aria-label="Return to Boundary"
								type="button" onClick={handleReturnToBoundary}
								className="tw-bg-white hover:tw-text-steel tw-text-xxs tw-text-charcoal"
							>
							Return to Boundary
							</button>
							<span className="tw-text-xxs tw-hidden lg:tw-block">
							or
							</span>
							<button
								aria-label="Select New Location"
								type="button"
								className="tw-bg-white hover:tw-text-slate tw-text-xxs tw-text-charcoal tw-hidden lg:tw-block"
								onClick={handleSelectNewLocation}
							>
							Select New Location
							</button>
						</div>
					</div>
				</div>
			)}
			<GoogleMapReact
				bootstrapURLKeys={{ key: config.GOOGLE_MAPS_API_KEY }}
				// center of us - doesn't matter I think, but having it static seems to prevent some odd centering
				defaultCenter={{ lat: 37.0902, lng: -95.7129 }}
				defaultZoom={8}
				center={center}
				zoom={zoom}
				onDrag={handleDrag}
				yesIWantToUseGoogleMapApiInternals
				// doesn't appear to be doing anything
				// hoverDistance={config.MARKER_SIZE / 4}
				onGoogleApiLoaded={( { map, maps } ) => handleApiLoaded( map, maps, hits )}
			/>
		</div>
	);
};

export default SearchMap;
